export type Locale = "en" | "fr";

export const SUPPORTED_LOCALES: Locale[] = ["en", "fr"];
export const DEFAULT_LOCALE: Locale = "en";

const translations = {
  en: {
    meta: {
      title: "Nellove — Invitation-only wedding planning app early access",
      description:
        "Request an invitation to Nellove early access — guests, RSVP, checklist, budget and timeline in one clear workspace.",
    },
    nav: {
      how: "How it works",
      couples: "For couples",
      planners: "For planners",
      join: "Request an invitation",
      signIn: "Sign in",
      checklist: "Wedding checklist",
      guides: "Guides",
      product: "The app",
    },

    hero: {
      eyebrow: "Early access · By invitation · Free forever",
      heading: "A calmer way to plan your wedding. Yours by invitation.",
      subtitle:
        "Guests, RSVP, checklist, budget and timeline, together in one clear workspace. Leave your email to request an invitation when early access opens.",
      learnMore: "See what you’ll get",
      imageAlt: "Warm wedding table setting with linen, dried florals and a planning notebook",
    },
    form: {
      emailLabel: "Email",
      emailPlaceholder: "you@example.com",
      nameLabel: "First name (optional)",
      namePlaceholder: "Your first name",
      segmentLabel: "This is for",
      segmentCouple: "I'm planning my wedding",
      segmentPlanner: "I'm a wedding planner or agency",
      cta: "Request an invitation",
      ctaLoading: "Sending request…",
      privacy:
        "No spam. Only your invitation and essential Nellove updates.",
      consent: "Email me about my Nellove invitation request and early access. I can unsubscribe anytime.",
      invalidEmail: "Please enter a valid email address.",
      genericError: "Something went wrong. Please try again.",
      successTitle: "Your invitation request is in",
      successBody: "We’ll email you if an early-access place opens. Signup alone does not grant access.",
      shareIntro: "Help Nellove grow: invite a couple or planner to request access too. Sharing won't change your place in line.",
      shareCopy: "Copy link",
      shareCopied: "Link copied",
      shareEmail: "Share by email",
      shareSubject: "Request an invitation to Nellove",
      shareText: "Nellove is a calm wedding planning workspace. Request an invitation for early access:",
      formLabel: "Request a Nellove invitation",
      requestTitle: "Request an invitation",
      requestBody: "Leave your email to request early access. Signup alone does not grant access.",
    },
    problem: {
      eyebrow: "Less juggling, more clarity",
      heading: "Your wedding plan should live in one place.",
      body: "Replace scattered spreadsheets, notes and message threads with a workspace made for the way weddings actually come together.",
      points: [
        "A spreadsheet for guests that breaks the moment plans change.",
        "Budgets living in notes, screenshots and bank apps.",
        "Decisions buried in group chats no one can search.",
      ],
    },
    solution: {
      eyebrow: "What you’ll get",
      heading: "Everything important, clearly connected.",
      subtitle: "Open Nellove and know what needs attention — without rebuilding your plan every week.",
      items: [
        {
          title: "Guests & RSVP",
          body: "Keep your guest list and RSVP responses together in one place.",
        },
        {
          title: "Date-aware checklist",
          body: "Tasks that know how close your date is, so you always see what matters this week.",
        },
        {
          title: "Budget in one place",
          body: "Estimated, quoted, paid and remaining due — clear at a glance.",
        },
      ],
    },
    how: {
      eyebrow: "Your early access",
      heading: "One email to request your invitation.",
      steps: [
        { title: "Request an invitation", body: "Leave your email and consent to hear from us." },
        { title: "Hear from us", body: "We'll email you when an early-access invitation is available. Signup alone does not grant access." },
        { title: "Access by invitation", body: "A place opens only when we email the invitation. Signup alone does not grant access." },
      ],
    },
    audience: {
      eyebrow: "Built for",
      heading: "Couples first, planners close behind.",
      couples: {
        title: "For couples",
        tagline: "Plan together, without the chaos.",
        body: "Nellove is built for couples who want one clear place to manage their wedding.",
        points: ["Shared access for both partners", "Guests and RSVP in one place", "Checklist tied to your date", "A budget you can actually read", "A clear day-of timeline"],
      },
      planners: {
        title: "For planners & agencies",
        tagline: "Built for multi-wedding work.",
        body: "Planners can keep each wedding’s guests, checklist, budget and timeline in its own workspace.",
        points: ["One workspace per wedding", "Guests and RSVP in one place", "A checklist tied to the wedding date", "A clear budget and day-of timeline"],
      },
    },
    trust: {
      heading: "Quietly serious about the details.",
      points: [
        "Built around real wedding workflows, not generic project tools.",
        "Available worldwide, in English and French.",
        "Designed for accessibility — WCAG-minded contrast and keyboard support.",
        "Your email is only ever used for launch updates.",
      ],
    },
    finalCta: {
      eyebrow: "By invitation",
      heading: "Make wedding planning feel manageable again.",
      subtitle: "Request your invitation with an email. We'll be in touch when early access is available.",
    },
    sticky: {
      label: "By invitation",
      cta: "Request an invitation",
    },
    footer: {
      tagline: "One calm workspace for your wedding.",
      privacy: "We only use waitlist emails for early-access updates.",
      rights: "All rights reserved.",
      facebook: "Nellove on Facebook",
      instagram: "Nellove on Instagram",
      social: "Follow along",
      location: "Montreal, Canada",
    },
    langSwitch: { label: "Language", en: "EN", fr: "FR" },
    a11y: {
      skipToContent: "Skip to main content",
      logoAlt: "Nellove",
    },
  },
  fr: {
    meta: {
      title: "Nellove — Accès anticipé sur invitation",
      description:
        "Demandez une invitation pour l’accès anticipé à Nellove — invités, RSVP, checklist, budget et déroulé dans un seul espace clair.",
    },
    nav: {
      how: "Comment ça marche",
      couples: "Pour les couples",
      planners: "Pour les wedding planners",
      join: "Demander une invitation",
      signIn: "Se connecter",
      checklist: "Checklist mariage",
      product: "L'application",
      guides: "Guides",
    },

    hero: {
      eyebrow: "Accès anticipé · Sur invitation · Gratuit pour toujours",
      heading: "Organisez votre mariage l’esprit libre. Sur invitation.",
      subtitle:
        "Invités, RSVP, checklist, budget et déroulé, réunis dans un seul espace clair. Laissez votre e-mail pour demander une invitation dès l’ouverture de l’accès anticipé.",
      learnMore: "Découvrez ce qui vous attend",
      imageAlt: "Table de mariage chaleureuse avec lin, fleurs séchées et carnet de planification",
    },
    form: {
      emailLabel: "E-mail",
      emailPlaceholder: "vous@exemple.com",
      nameLabel: "Prénom (facultatif)",
      namePlaceholder: "Votre prénom",
      segmentLabel: "Vous êtes",
      segmentCouple: "J'organise mon mariage",
      segmentPlanner: "Je suis wedding planner ou agence",
      cta: "Demander une invitation",
      ctaLoading: "Envoi en cours…",
      privacy:
        "Aucun spam. Seulement votre invitation et les actualités essentielles de Nellove.",
      consent: "Envoyez-moi des nouvelles de ma demande d’invitation Nellove et de l’accès anticipé. Je peux me désinscrire en tout temps.",
      invalidEmail: "Veuillez saisir une adresse e-mail valide.",
      genericError: "Une erreur est survenue. Merci de réessayer.",
      successTitle: "Votre demande d’invitation est reçue",
      successBody: "Nous vous écrirons si une place d’accès anticipé se libère. L’inscription seule ne donne pas accès.",
      shareIntro: "Faites connaître Nellove : invitez un couple ou un planner à demander l’accès. Le partage ne change pas votre place dans la liste.",
      shareCopy: "Copier le lien",
      shareCopied: "Lien copié",
      shareEmail: "Partager par e-mail",
      shareSubject: "Demandez une invitation Nellove",
      shareText: "Nellove est un espace serein pour organiser votre mariage. Demandez une invitation pour l’accès anticipé :",
      formLabel: "Demander une invitation Nellove",
      requestTitle: "Demander une invitation",
      requestBody: "Laissez votre e-mail pour demander l’accès anticipé. L’inscription seule ne donne pas accès.",
    },
    problem: {
      eyebrow: "Moins d’outils, plus de clarté",
      heading: "Votre mariage mérite un seul espace d’organisation.",
      body: "Remplacez les tableurs, notes et conversations éparpillés par un espace pensé pour la réalité d’un mariage.",
      points: [
        "Un tableur d'invités qui casse au moindre changement.",
        "Un budget éparpillé entre notes, captures d'écran et applis bancaires.",
        "Des décisions perdues dans des conversations impossibles à retrouver.",
      ],
    },
    solution: {
      eyebrow: "Ce qui vous attend",
      heading: "Tout l’essentiel, clairement relié.",
      subtitle: "Ouvrez Nellove et sachez quoi faire — sans reconstruire votre plan chaque semaine.",
      items: [
        {
          title: "Invités & RSVP",
          body: "Gardez la liste d’invités et les réponses RSVP au même endroit.",
        },
        {
          title: "Checklist selon la date",
          body: "Des tâches qui connaissent votre date, pour voir ce qui compte vraiment cette semaine.",
        },
        {
          title: "Budget centralisé",
          body: "Estimé, soumissionné, payé et reste dû — lisible d’un coup d’œil.",
        },
      ],
    },
    how: {
      eyebrow: "Votre accès anticipé",
      heading: "Un e-mail pour demander votre invitation.",
      steps: [
        { title: "Demandez une invitation", body: "Laissez votre e-mail et acceptez de recevoir nos nouvelles." },
        { title: "Recevez de nos nouvelles", body: "Nous vous écrirons quand une invitation sera disponible. L'inscription seule ne donne pas accès." },
        { title: "Accès sur invitation", body: "Une place s’ouvre uniquement lorsque nous envoyons l’invitation par e-mail. L’inscription seule ne donne pas accès." },
      ],
    },
    audience: {
      eyebrow: "Conçu pour",
      heading: "Les couples d'abord, les planners juste après.",
      couples: {
        title: "Pour les couples",
        tagline: "Organisez à deux, sans le chaos.",
        body: "Nellove est conçu pour les couples qui veulent gérer leur mariage dans un seul espace clair.",
        points: ["Accès partagé pour les deux partenaires", "Invités et RSVP au même endroit", "Checklist liée à votre date", "Un budget enfin lisible", "Un déroulé clair pour le jour J"],
      },
      planners: {
        title: "Pour les planners & agences",
        tagline: "Pensé pour le multi-mariages.",
        body: "Les professionnels peuvent réunir invités, checklist, budget et déroulé dans un espace propre à chaque mariage.",
        points: ["Un espace par mariage", "Invités et RSVP au même endroit", "Une checklist liée à la date", "Un budget et un déroulé clairs pour le jour J"],
      },
    },
    trust: {
      heading: "Sérieux sur les détails, sans en faire trop.",
      points: [
        "Pensé pour de vrais déroulés de mariage, pas pour un outil générique.",
        "Disponible partout dans le monde, en français et en anglais.",
        "Conçu pour l'accessibilité — contrastes et navigation clavier soignés.",
        "Votre e-mail sert uniquement aux actualités du lancement.",
      ],
    },
    finalCta: {
      eyebrow: "Sur invitation",
      heading: "Retrouvez une organisation de mariage enfin maîtrisée.",
      subtitle: "Demandez votre invitation avec un e-mail. Nous vous écrirons quand l’accès anticipé sera disponible.",
    },
    sticky: {
      label: "Sur invitation",
      cta: "Demander une invitation",
    },
    footer: {
      tagline: "Un espace serein pour votre mariage.",
      privacy: "Les e-mails de la liste servent uniquement aux actualités de l’accès anticipé.",
      rights: "Tous droits réservés.",
      facebook: "Nellove sur Facebook",
      instagram: "Nellove sur Instagram",
      social: "Nous suivre",
      location: "Montréal, Canada",
    },
    langSwitch: { label: "Langue", en: "EN", fr: "FR" },
    a11y: {
      skipToContent: "Aller au contenu principal",
      logoAlt: "Nellove",
    },
  },
} as const;

export type Translations = (typeof translations)["en"];

export const getTranslations = (locale: Locale): Translations =>
  (translations[locale] ?? translations[DEFAULT_LOCALE]) as Translations;

export const detectLocale = (): Locale => {
  if (typeof navigator === "undefined") return DEFAULT_LOCALE;
  const langs = [navigator.language, ...(navigator.languages ?? [])];
  for (const lang of langs) {
    const code = lang?.slice(0, 2).toLowerCase();
    if (code && SUPPORTED_LOCALES.includes(code as Locale)) return code as Locale;
  }
  return DEFAULT_LOCALE;
};

export default translations;
